// src/pages/UserDashboard.jsx
 
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
 
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Tooltip
} from "@mui/material";
 
import {
  Logout as LogoutIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  TipsAndUpdates as TipsIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
 
export default function UserDashboard() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
 

  useEffect(() => {
    const data = sessionStorage.getItem("userData");
    if (!data) {
      navigate("/user/login");
      return;
    }
    const parsed = JSON.parse(data);
    // If status is resume, redirect and call API
    if (parsed?.latest_expert?.status === "resume" && parsed?.latest_expert?.resume) {
      // Save session_token and assessment_id if needed
      sessionStorage.setItem("sessionToken", parsed.latest_expert.resume.session_token);
      sessionStorage.setItem("assessmentId", parsed.latest_expert.resume.assessment_id);
      // Clear any stale assessmentData to force fresh context load
      sessionStorage.removeItem("assessmentData");
      navigate("/assessment/questions");
      return;
    }
    setUserData(parsed);
  }, [navigate]);
 
  if (!userData) return null;
 
  const handleLogout = () => {
    sessionStorage.removeItem("userData");
    navigate("/");
  };
 
  const expert = userData.latest_expert;
 
  const downloadReport = () => {
    // Get data from sessionStorage
    const localUserData = JSON.parse(sessionStorage.getItem('userData'));
    const userRegistrationData = JSON.parse(sessionStorage.getItem('userRegistrationData'));
    
    if (!localUserData?.latest_expert?.result || !userRegistrationData) {
      alert('Assessment data not found. Please complete the assessment first.');
      return;
    }
 
    const result = localUserData.latest_expert.result;
    const doc = new jsPDF();
   
    // Colors - Updated to theme color
    const primaryBlue = [0, 68, 148]; // #004494
    const lightBlue = [230, 242, 255]; // Light blue background
    const darkGray = [51, 51, 51];
   
    // Get current date
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
 
    // ===== PAGE 1 =====
   
    // Header
    doc.setFillColor(...primaryBlue);
    doc.rect(0, 0, 210, 35, 'F');
   
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('RAISE Index', 20, 18);
   
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Assessment Report', 20, 26);
   
    doc.setFontSize(9);
    doc.text(currentDate, 155, 18);
 
    // Organization Name
    doc.setTextColor(...darkGray);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(userRegistrationData.organisationName || 'Organization', 20, 50);
 
    // Score Circle Background
    doc.setFillColor(...lightBlue);
    doc.roundedRect(20, 60, 170, 50, 3, 3, 'F');
 
    // Score Circle (simulated)
    const centerX = 55;
    const centerY = 85;
    const radius = 20;
   
    doc.setDrawColor(...primaryBlue);
    doc.setLineWidth(3);
    doc.circle(centerX, centerY, radius, 'S');
   
    doc.setTextColor(...darkGray);
    doc.setFontSize(32);
    doc.setFont('helvetica', 'bold');
    doc.text(Math.round(result.overall_score).toString(), centerX, centerY + 2, { align: 'center' });
   
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('/ 100', centerX, centerY + 10, { align: 'center' });
 
    // Maturity Level
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryBlue);
    doc.text(result.maturity_level || 'DEVELOPING', 100, 80);
   
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Practices documented but', 100, 88);
    doc.text('implementation varies', 100, 93);
 
    // Calculate penalty if score is below certain thresholds
    const highRiskPrinciples = result.principle_breakdown.filter(item =>
      item.average < 0.5 && item.weight > 0.1
    );
    if (highRiskPrinciples.length > 0) {
      const percentage = Math.round((highRiskPrinciples.length / result.principle_breakdown.length) * 100);
      doc.setFontSize(8);
      doc.setTextColor(...primaryBlue);
      doc.text(`Note: ${percentage}% penalty applied due to high-risk`, 100, 102);
      doc.text('principles below threshold', 100, 107);
    }
 
    // Score Breakdown Table
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text('Score Breakdown by Principle', 20, 125);
 
    // Prepare table data with progress bars (no value in progress column)
    const tableData = result.principle_breakdown.map(item => {
      const average = item.average.toFixed(2);
      const weight = item.weight.toFixed(2);
      const contribution = item.contribution.toFixed(2);
      
      return [
        item.principle,
        average,
        weight,
        contribution,
        '' // Empty string for progress column, we'll draw the bar manually
      ];
    });
 
    autoTable(doc, {
      startY: 130,
      head: [['Principle', 'Average', 'Weight', 'Contribution', 'Progress']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: primaryBlue, // Theme color for header
        textColor: [255, 255, 255], // White text for contrast
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'left',
        font: 'helvetica'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: darkGray,
        fillColor: [248, 249, 250], // Very light gray for alternating rows
        font: 'helvetica'
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255] // White for alternating rows
      },
      columnStyles: {
        0: { cellWidth: 60 }, // Principle
        1: { cellWidth: 25, halign: 'center' }, // Average
        2: { cellWidth: 25, halign: 'center' }, // Weight
        3: { cellWidth: 25, halign: 'center' }, // Contribution
        4: { cellWidth: 45, halign: 'center' } // Progress
      },
      margin: { left: 20, right: 20 },
      didDrawCell: function (data) {
        // Draw progress bars in the Progress column (column 4)
        if (data.column.index === 4 && data.section === 'body') {
          const progress = result.principle_breakdown[data.row.index].average; // Get the average value
          
          // Progress bar dimensions - very thin with rounded edges
          const barWidth = 35;
          const barHeight = 1.5; // Very thin bar
          const barX = data.cell.x + 5;
          const barY = data.cell.y + (data.cell.height / 2) - (barHeight / 2);
          const radius = barHeight / 2; // Radius for rounded edges
          
          // Background bar (very light gray) with rounded edges
          doc.setFillColor(235, 235, 235);
          doc.roundedRect(barX, barY, barWidth, barHeight, radius, radius, 'F');
          
          // Progress bar color based on value
          let progressColor;
          if (progress >= 0.8) {
            progressColor = [34, 139, 34]; // Forest Green
          } else if (progress >= 0.6) {
            progressColor = [255, 140, 0]; // Dark Orange
          } else {
            progressColor = primaryBlue; // Use theme color for lower scores
          }
          
          // Filled progress bar with rounded edges
          doc.setFillColor(...progressColor);
          const filledWidth = barWidth * progress;
          if (filledWidth > 0) {
            // For very small widths, just draw a simple rectangle to avoid visual issues
            if (filledWidth < radius * 2) {
              doc.rect(barX, barY, filledWidth, barHeight, 'F');
            } else {
              doc.roundedRect(barX, barY, filledWidth, barHeight, radius, radius, 'F');
            }
          }
        }
      }
    });
 
    // Page number
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Page 1 of 2', 180, 285, { align: 'right' });
 
    // ===== PAGE 2 =====
    doc.addPage();
 
    let yPosition = 25;
 
    // Strengths Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text('Strengths', 20, yPosition);
 
    yPosition += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
   
    // Use strengths and improvements arrays directly from result
    const strengths = result.strengths || [];
    strengths.forEach(strength => {
      doc.text(`• ${strength}`, 25, yPosition);
      yPosition += 6;
    });

    yPosition += 5;

    // Areas for Improvement Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Areas for Improvement', 110, 25);

    let yPosition2 = 35;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const improvements = result.improvements || [];
    improvements.forEach(improvement => {
      doc.text(`• ${improvement}`, 115, yPosition2);
      yPosition2 += 6;
    });
 
    yPosition = Math.max(yPosition, yPosition2) + 10;
 
    // Recommendations Section - Dynamic based on improvements
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text('Recommendations', 20, yPosition);

    yPosition += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // Suggestion mapping
    const suggestionMap = {
      "Valid and Reliable": "Strengthen validation tests, ground-truth checks, and benchmark reviews.",
      "Safe": "Introduce human-in-the-loop for high-risk cases and define escalation/kill-switch paths.",
      "Secure and Resilient": "Harden access, enable detailed logging, and rehearse recovery scenarios.",
      "Accountable and Transparent": "Clarify ownership, add audit trails, and improve stakeholder reporting.",
      "Explainable and Interpretable": "Provide explanation UIs or local feature importance for key decisions.",
      "Fair with Bias Actively Managed": "Run fairness tests for key cohorts; document mitigations and trade-offs.",
      "Privacy-First": "Minimize personal data, add consent/opt-out, and review PETs for production.",
      "Universality and Inclusivity": "Improve accessibility, language support, and low-connectivity experience."
    };

    // Get recommendations based on areas for improvement
    const dynamicRecommendations = improvements.map(improvement => {
      return suggestionMap[improvement] || `Improve ${improvement} practices and implementation.`;
    });

    // If no improvements, show default recommendations
    const recommendationsToShow = dynamicRecommendations.length > 0 ? dynamicRecommendations : [
      'Continue maintaining current high standards across all principles.',
      'Regular review and monitoring of AI systems.',
      'Stay updated with latest responsible AI practices.'
    ];

    recommendationsToShow.forEach((rec, index) => {
      doc.text(`${index + 1}. ${rec}`, 20, yPosition);
      yPosition += 8;
    });
 
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('RAISE Index Assessment Report', 20, 285);
    doc.text('Confidential', 100, 285);
    doc.text('Page 2 of 2', 180, 285, { align: 'right' });
 
    // Save PDF
    const fileName = `RAISE_Assessment_Report_${userRegistrationData.organisationName || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };
 
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8f9fa", fontFamily: 'Helvetica, Arial, sans-serif' }}>
      {/* HEADER */}
      <Box sx={{ bgcolor: "#004494", py: 3, color: "white" }}>
        <Container
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Typography  
            variant="h5" 
            sx={{ 
              fontWeight: 600, 
              color: 'white',
              fontFamily: 'Helvetica, Arial, sans-serif'
            }}
          >
            Welcome, {userData.full_name}
          </Typography>
 
          <Button
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            variant="outlined"
            sx={{ 
              color: "white", 
              borderColor: "white",
              fontFamily: 'Helvetica, Arial, sans-serif',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.7)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Logout
          </Button>
        </Container>
      </Box>
 
      <Container sx={{ py: 5 }}>
        {/* ------------------ PENDING REVIEW ------------------ */}
        {expert.status === "pending" && (
          <Paper sx={{ p: 4, textAlign: "center" }} elevation={3}>
            <AssessmentIcon sx={{ fontSize: 60, color: "#ff9800", mb: 2 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 300, 
                mb: 2,
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '32px',
                color: '#000'
              }}
            >
              Review in Progress
            </Typography>
            <Typography 
              sx={{ 
                maxWidth: 600, 
                mx: "auto", 
                mb: 3,
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              {expert.message}
            </Typography>
          </Paper>
        )}
 
        {/* ------------------ REVIEW AVAILABLE ------------------ */}
        {expert.status === "available" && (
          <>
            {/* SCORE CARD */}
            <Paper sx={{ p: 4, mb: 5, textAlign: "center" }} elevation={3}>
              <AssessmentIcon
                sx={{ fontSize: 60, color: "#4caf50", mb: 2 }}
              />
 
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 300,
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  fontSize: '32px',
                  color: '#000'
                }}
              >
                Your RAISE Score: {expert.result.overall_score}%
              </Typography>
 
              <Chip
                label={expert.result.maturity_level}
                color={
                  {
                    LEADING: "info",
                    ADVANCED: "success",
                    DEVELOPING: "warning",
                    CRITICAL: "error"
                  }[expert.result.maturity_level]
                }
                sx={{ 
                  fontSize: "1.1rem", 
                  mt: 2,
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
              />
 
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadReport}
                  sx={{
                    backgroundColor: '#004494',
                    color: 'white',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    '&:hover': {
                      backgroundColor: '#003366'
                    }
                  }}
                >
                  Download PDF Report
                </Button>
              </Box>
            </Paper>
 
            {/* ------------------ PRINCIPLE BREAKDOWN ------------------ */}
            <Typography
              variant="h5"
              sx={{ 
                fontWeight: 500, 
                mb: 3, 
                textAlign: "center",
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '24px',
                color: '#000'
              }}
            >
              Principle Breakdown
            </Typography>
 
            <Grid container spacing={3} sx={{ mb: 5, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {expert.result.principle_breakdown.map((item, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card
                    elevation={3}
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      transition: "0.3s",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: 6 }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Tooltip title={item.principle} arrow>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#004494",
                            fontWeight: 500,
                            mb: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 120,
                            cursor: 'pointer',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontSize: '18px'
                          }}
                        >
                          {item.principle.split(' ')[0]}
                        </Typography>
                      </Tooltip>
 
                      <Divider sx={{ mb: 2 }} />
 
                      <Typography
                        sx={{
                          fontFamily: 'Helvetica, Arial, sans-serif',
                          fontSize: '14px',
                          fontWeight: 400
                        }}
                      >
                        <strong>Score:</strong>{" "}
                        {(item.average * 100).toFixed(1)}%
                      </Typography>
 
                      <Typography 
                        sx={{ 
                          mb: 1,
                          fontFamily: 'Helvetica, Arial, sans-serif',
                          fontSize: '14px',
                          fontWeight: 400
                        }}
                      >
                        <strong>Contribution:</strong>{" "}
                        {item.contribution.toFixed(2)}
                      </Typography>
 
                      <Chip
                        label={item.category.toUpperCase()}
                        color={item.category === "strength" ? "success" : "warning"}
                        size="small"
                        sx={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
 
            {/* ------------------ 3 COLUMN SUMMARY ------------------ */}
            <Grid container spacing={3} sx={{ mb: 5, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {/* STRENGTHS */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #E8F5E9, #FFFFFF)",
                    transition: "0.3s",
                    "&:hover": { boxShadow: 6, transform: "translateY(-4px)" }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <TrendingUpIcon sx={{ color: "#4caf50", mr: 1 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500,
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '18px'
                      }}
                    >
                      Strengths
                    </Typography>
                  </Box>
 
                  {expert.result.strengths.map((s, i) => (
                    <Typography 
                      key={i} 
                      sx={{ 
                        mb: 1,
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '14px',
                        fontWeight: 400
                      }}
                    >
                      • {s}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
 
              {/* AREAS OF IMPROVEMENT */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #FFF3E0, #FFFFFF)",
                    transition: "0.3s",
                    "&:hover": { boxShadow: 6, transform: "translateY(-4px)" }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <WarningIcon sx={{ color: "#ff9800", mr: 1 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500,
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '18px'
                      }}
                    >
                      Areas for Improvement
                    </Typography>
                  </Box>
 
                  {expert.result.improvements.map((s, i) => (
                    <Typography 
                      key={i} 
                      sx={{ 
                        mb: 1,
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '14px',
                        fontWeight: 400
                      }}
                    >
                      • {s}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
 
              {/* SUGGESTIONS */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 3,
                    background: "linear-gradient(135deg, rgba(0, 68, 148, 0.1), #FFFFFF)",
                    transition: "0.3s",
                    "&:hover": { boxShadow: 6, transform: "translateY(-4px)" }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <TipsIcon sx={{ color: "#004494", mr: 1 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500,
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '18px'
                      }}
                    >
                      Suggested Actions
                    </Typography>
                  </Box>
 
                  {expert.result.suggestions.map((s, i) => (
                    <Typography 
                      key={i} 
                      sx={{ 
                        mb: 1,
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '14px',
                        fontWeight: 400
                      }}
                    >
                      ✔ {s}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
}